import { BarahPayApiError } from './http';
import type { TranslationResources } from './i18n';

/** The translation function shape both apps get from `useTranslation`. */
type Translate = (key: string, options?: Record<string, unknown>) => string;

type ErrorCode = keyof TranslationResources['errors'];

/**
 * Turns a thrown value into text in the reader's language.
 *
 * The API answers a failure with a stable code from the Barah Pay catalogue (spec section 52) and an
 * English `detail` line; it does not translate, because `Accept-Language` reaches no localisation on
 * the server. Rendering `detail` therefore put an English sentence under an Arabic heading on every
 * failure in the portal. Keying off the code instead makes the message follow the chosen language, and
 * leaves the server's text as a diagnostic rather than the thing the operator reads.
 *
 * An unrecognised code — a newer API than this bundle — falls back to the generic message rather than
 * printing a raw code at someone.
 */
export function translateApiError(error: unknown, t: Translate): string {
  if (!error) {
    return t('errors.UNKNOWN');
  }

  const code = error instanceof BarahPayApiError ? error.errorCode : undefined;

  if (code) {
    const key = `errors.${code}`;
    const translated = t(key);

    // i18next returns the key itself when nothing matches, which is how an unmapped code is detected.
    if (translated !== key) {
      return translated;
    }
  }

  return t('errors.UNKNOWN');
}

/**
 * True when the failure is worth offering a retry for: the request either never arrived or hit
 * something the server itself described as temporary.
 */
export function isRetryableApiError(error: unknown): boolean {
  return error instanceof BarahPayApiError && error.isTransient;
}

/**
 * Translates a description the platform seeded itself, falling back to the stored text.
 *
 * The seeder writes English prose into the database for the built-in roles and the feature flags, so
 * those two tables stayed English in an otherwise Arabic portal and no resource file could reach them.
 * The keys are stable, so the portal can translate them on the way out.
 *
 * The fallback is the important half: a role an operator created carries a description *they* wrote,
 * and replacing that with a guess would be worse than leaving it. Only a recognised key is translated.
 */
export function translateSeededDescription(
  namespace: 'roleDescriptions' | 'flagDescriptions' | 'settingDescriptions' | 'settingCategories',
  key: string | null | undefined,
  storedDescription: string | null | undefined,
  t: Translate,
): string {
  if (!key) {
    return storedDescription ?? '';
  }

  // Setting keys are dotted ("payments.expiry.minutes") and i18next reads a dot as a nesting
  // separator, so the resources store them with underscores and the lookup matches that.
  const lookup = `${namespace}.${key.replace(/\./g, '_')}`;
  const translated = t(lookup);

  // i18next echoes the key back when nothing matches, which is how an unseeded row is detected.
  return translated === lookup ? (storedDescription ?? '') : translated;
}

export type { ErrorCode };
